import { ref } from 'vue';
import { ServiceLocator } from '@/services/ServiceLocator';
import type { ResponseCheckResetPasswordToken } from '@/infrastructure/api/client_typescript';

export function usePasswordReset() {
  const loading = ref(false);
  const error = ref<string | null>(null);
  const success = ref(false);

  const passwordResetService = ServiceLocator.getPasswordResetService();

  async function sendResetEmail(email: string) {
    loading.value = true;
    error.value = null;
    success.value = false;

    try {
      await passwordResetService.resetPassword(email);
      success.value = true;
    } catch (err: unknown) {
      error.value = err instanceof Error ? err.message : 'Something went wrong';
    } finally {
      loading.value = false;
    }
  }

  async function verifyToken(
    token: string
  ): Promise<ResponseCheckResetPasswordToken | null> {
    loading.value = true;
    error.value = null;

    try {
      const result = await passwordResetService.checkResetPasswordToken(token);
      return result;
    } catch (err: unknown) {
      error.value =
        err instanceof Error ? err.message : 'Invalid or expired token.';
      return null;
    } finally {
      loading.value = false;
    }
  }

  async function finishReset(token: string, newPassword: string) {
    loading.value = true;
    error.value = null;
    success.value = false;

    try {
      await passwordResetService.finishResetPassword(token, newPassword);
      success.value = true;
    } catch (err: unknown) {
      error.value =
        err instanceof Error ? err.message : 'Could not reset password.';
    } finally {
      loading.value = false;
    }
  }

  return {
    sendResetEmail,
    verifyToken,
    finishReset,
    loading,
    error,
    success,
  };
}
